import type { SearchableAppPage } from '@/config/appNavigation'
import { Path } from '@/routes/paths'
import type { EntityHit } from '@features/search/api/contracts'
import { normalizeFavoritePath, type FavoriteDescription } from './favorites'

/** What the sidebar's search finds: anything a user could favorite. */
export type SidebarSearchKind = 'page' | 'table' | 'view'

/** A place the sidebar's search can take you, read as a favorite is: by its trail. */
export interface SidebarSearchResult {
  key: string
  kind: SidebarSearchKind
  /** Where it goes; also its favorite's address, so its heart is that favorite's. */
  path: string
  /** Its trail, step by step: its area first, its own page last (FavoriteTrail). */
  parts: string[]
  /** The area it belongs to, by its first segment (`tables`), for its icon. */
  area: string
}

const firstSegment = (path: string) => path.split('/').filter(Boolean)[0] ?? ''

/**
 * An app page (a menu's page, an area's own page) as a result, its trail its
 * area then its own name (Settings › Organization), or just its name when it
 * is its area's page.
 */
export function pageSearchResult(page: SearchableAppPage): SidebarSearchResult {
  // One address per page (`/tables/` is `/tables`), as favorites and recent visits keep it.
  const path = normalizeFavoritePath(page.path)
  return {
    key: `page:${path}`,
    kind: 'page',
    path,
    parts: page.name === page.module ? [page.name] : [page.module, page.name],
    area: firstSegment(page.path),
  }
}

/** A table, as its favorite reads (Tables › Accounts). */
export function tableSearchResult(
  table: { id: string; name: string },
  tablesLabel: string
): SidebarSearchResult {
  const path = Path.tableRecords(table.id)
  return {
    key: `table:${table.id}`,
    kind: 'table',
    path,
    parts: [tablesLabel, table.name],
    area: 'tables',
  }
}

/**
 * A place the user opened lately, as its favorite reads, named from its
 * page's metadata (describeFavorite); null when it's gone or switched off.
 */
export function recentSearchResult(description: FavoriteDescription): SidebarSearchResult | null {
  if (description.status === 'gone' || description.status === 'unavailable') return null
  const kind: SidebarSearchKind = description.isView
    ? 'view'
    : description.area === 'tables' && description.parts.length > 1
      ? 'table'
      : 'page'
  return {
    key: `recent:${description.path}`,
    kind,
    path: description.path,
    parts: description.parts,
    area: description.area,
  }
}

/** A view, as its favorite reads (Tables › Accounts › All Records); null without its table. */
export function viewSearchResult(hit: EntityHit, tablesLabel: string): SidebarSearchResult | null {
  if (!hit.table_id) return null
  return {
    key: `view:${hit.table_id}:${hit.id}`,
    kind: 'view',
    path: Path.tableViewRecords(hit.table_id, hit.id),
    parts: [tablesLabel, hit.table_name ?? '', hit.name],
    area: 'tables',
  }
}

const WORD_BREAK = /[\s\-_/()&.,:›]+/

/**
 * How well `name` matches `query`, lower is better, or null when it doesn't:
 * the whole name (0), its start (1), the start of one of its words (2), or
 * anywhere in it (3). Case doesn't matter.
 */
export function matchRank(name: string, query: string): number | null {
  const text = name.toLocaleLowerCase()
  const term = query.trim().toLocaleLowerCase()
  if (!term) return null
  if (text === term) return 0
  if (text.startsWith(term)) return 1
  if (text.split(WORD_BREAK).some((word) => word.startsWith(term))) return 2
  if (text.includes(term)) return 3
  return null
}

/** A match found only in a result's trail (its area, a view's table): after any by its own name. */
const TRAIL_MATCH_RANK = 4

const KIND_ORDER: Record<SidebarSearchKind, number> = { page: 0, table: 1, view: 2 }

/**
 * Places to browse, A to Z by their whole trail (so an area's pages come
 * together: Settings › Organization, Settings › Permissions), each once,
 * leaving out the addresses in `skip` (the ones already listed as recent).
 */
export function alphabeticalSidebarSearch(
  results: readonly SidebarSearchResult[],
  skip: ReadonlySet<string> = new Set()
): SidebarSearchResult[] {
  const seen = new Set(skip)
  const kept: { result: SidebarSearchResult; trail: string }[] = []
  for (const result of results) {
    if (seen.has(result.path)) continue
    seen.add(result.path)
    kept.push({ result, trail: result.parts.join(' › ') })
  }
  return kept
    .sort((a, b) =>
      a.trail.localeCompare(b.trail, undefined, { sensitivity: 'base', numeric: true })
    )
    .map(({ result }) => result)
}

/**
 * The results that match `query`, best first, as one flat list: by how well
 * their own name (the trail's last step) matches, then by a match anywhere in
 * their trail (`Tables` finds every table); ties go to the one opened most
 * lately (`recency`: each address's place among the recent visits, newest
 * 0), then the shorter name, then pages, tables, views, then A to Z.
 * A view counts only by its own name: one found by its table's name is just
 * another way into a table the list already has. Each place once (a page and
 * a table can share an address).
 */
export function rankSidebarSearch(
  results: readonly SidebarSearchResult[],
  query: string,
  recency: ReadonlyMap<string, number> = new Map()
): SidebarSearchResult[] {
  const lately = (result: SidebarSearchResult) =>
    recency.get(result.path) ?? Number.POSITIVE_INFINITY
  const seen = new Set<string>()
  const scored: { result: SidebarSearchResult; rank: number; name: string }[] = []
  for (const result of results) {
    if (seen.has(result.path)) continue
    const name = result.parts[result.parts.length - 1] ?? ''
    const own = matchRank(name, query)
    const rank =
      own ??
      (result.kind !== 'view' &&
      result.parts.slice(0, -1).some((part) => matchRank(part, query) !== null)
        ? TRAIL_MATCH_RANK
        : null)
    if (rank === null) continue
    seen.add(result.path)
    scored.push({ result, rank, name })
  }
  return scored
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        // Infinity - Infinity is NaN, which || passes over as a tie.
        lately(a.result) - lately(b.result) ||
        a.name.length - b.name.length ||
        KIND_ORDER[a.result.kind] - KIND_ORDER[b.result.kind] ||
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true })
    )
    .map(({ result }) => result)
}
